import { formatReportDateYmd } from '../utils/reportPatientJoins';

/** Local YYYY-MM-DD from a Date. */
export function ymdLocal(d) {
  if (!d || Number.isNaN(d.getTime?.() ?? NaN)) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function isRpmProgram(program) {
  return String(program?.serviceTypeAbbreviation || '').toUpperCase() === 'RPM';
}

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Format YYYY-MM-DD (or date-like) as MM/DD/YY. */
export function formatShortMdY(value) {
  const s = formatReportDateYmd(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '';
  const [y, m, d] = s.split('-');
  return `${m}/${d}/${y.slice(2)}`;
}

/** Whether an RPM session is enrolled (open) at the start of startYmd. */
export function sessionOpenAtStart(session, startYmd) {
  const enroll = formatReportDateYmd(session?.enrollmentDate);
  if (!enroll || enroll >= startYmd) return false;
  const discharge = formatReportDateYmd(session?.dischargeDate);
  if (discharge && discharge < startYmd) return false;
  return true;
}

/** Whether an RPM session is enrolled (open) at the end of endYmd. */
export function sessionOpenAtEnd(session, endYmd) {
  const enroll = formatReportDateYmd(session?.enrollmentDate);
  if (!enroll || enroll > endYmd) return false;
  const discharge = formatReportDateYmd(session?.dischargeDate);
  if (discharge && discharge <= endYmd) return false;
  return true;
}

/**
 * Audit actions that end a hold. Discharge, re-enroll and provider change
 * clear the program's hold fields without writing `hold_lifted`.
 */
export const HOLD_ENDING_ACTIONS = new Set([
  'hold_lifted', 'discharge', 're_enroll', 'enrollment', 'program_closed', 'program_reopened',
]);

function holdSessionFloor(opts) {
  const ymd = formatReportDateYmd(opts?.sessionFloorYmd);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function sessionTerminalYmd(session) {
  return [formatReportDateYmd(session?.dischargeDate), formatReportDateYmd(session?.closedDate)]
    .filter(Boolean).sort()[0] || '';
}

/**
 * Latest discharge/closed date among sessions before `session` (by
 * sessionNumber, else array order). Holds created before then belong to an
 * earlier session.
 */
export function priorSessionEndYmd(sessions, session) {
  const list = Array.isArray(sessions) ? sessions : [];
  const idx = list.indexOf(session);
  const num = Number(session?.sessionNumber) || 0;
  let floor = '';
  list.forEach((s, i) => {
    if (s === session) return;
    const otherNum = Number(s?.sessionNumber) || 0;
    const earlier = num && otherNum ? otherNum < num : idx >= 0 && i < idx;
    if (!earlier) return;
    const terminal = sessionTerminalYmd(s);
    if (terminal > floor) floor = terminal;
  });
  return floor;
}

/**
 * Latest hold or hold-ending action at or before cutoff (exclusive or
 * inclusive). With `sessionFloorYmd`, audits created before that day are
 * ignored, so a hold from an earlier session cannot carry into a later
 * (possibly backdated) one.
 * @param {'lt'|'lte'} mode
 * @param {{ sessionFloorYmd?: string }} [opts]
 */
export function latestHoldAction(auditsForProgram, cutoff, mode, opts) {
  const floor = holdSessionFloor(opts);
  let latest = null;
  for (const a of auditsForProgram) {
    if (a.action !== 'hold_set' && !HOLD_ENDING_ACTIONS.has(a.action)) continue;
    const at = toDate(a.createdAt);
    if (!at) continue;
    if (floor && at < floor) continue;
    const ok = mode === 'lt' ? at < cutoff : at <= cutoff;
    if (!ok) continue;
    if (!latest || at > toDate(latest.createdAt)) latest = a;
  }
  return latest;
}

export function isOnHoldAt(auditsForProgram, cutoff, mode, opts) {
  const latest = latestHoldAction(auditsForProgram, cutoff, mode, opts);
  return latest?.action === 'hold_set';
}

/** Hold check scoped to one session of a program (`sessions` = program.sessions). */
export function isSessionOnHoldAt(auditsForProgram, sessions, session, cutoff, mode) {
  return isOnHoldAt(auditsForProgram, cutoff, mode, { sessionFloorYmd: priorSessionEndYmd(sessions, session) });
}

/**
 * Find the open RPM session for a program at an as-of instant.
 * @param {'start'|'end'} mode
 */
export function findOpenSessionAt(sessions, ymd, mode) {
  const list = Array.isArray(sessions) ? sessions : [];
  const open = list.filter((s) => (mode === 'start' ? sessionOpenAtStart(s, ymd) : sessionOpenAtEnd(s, ymd)));
  if (!open.length) return null;
  open.sort((a, b) => (Number(b.sessionNumber) || 0) - (Number(a.sessionNumber) || 0));
  return open[0];
}

/** Group hold audits by patientId|programDocId. */
export function groupHoldAuditsByProgram(holdAudits) {
  const map = new Map();
  (holdAudits || []).forEach((a) => {
    const key = `${a.patientId || ''}|${a.programDocId || ''}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(a);
  });
  return map;
}

/**
 * Whether a patient has an active (enrolled, not on hold) RPM program at as-of.
 * Returns the open session + program when active.
 */
export function findActiveRpmAtAsOf(programs, auditsByProgram, patientId, asOf) {
  const mode = asOf?.mode === 'start' ? 'start' : 'end';
  const ymd = asOf?.ymd;
  const cutoff = asOf?.cutoffDate;
  const holdMode = mode === 'start' ? 'lt' : 'lte';
  if (!ymd || !cutoff) return null;

  const list = Array.isArray(programs) ? programs : [];
  for (const program of list) {
    if (!isRpmProgram(program)) continue;
    const session = findOpenSessionAt(program.sessions, ymd, mode);
    if (!session) continue;
    const programAudits = auditsByProgram.get(`${patientId}|${program.id || ''}`) || [];
    if (isSessionOnHoldAt(programAudits, program.sessions, session, cutoff, holdMode)) continue;
    return { program, session };
  }
  return null;
}

function ymdToUtc(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return null;
  const date = new Date(`${ymd}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * RPM enrollment overlap for billing. Discharge/closed dates are inclusive
 * for activity, while period-end census status remains handled separately.
 */
export function rpmEnrollmentWindowInPeriod(programs, auditsByProgram, patientId, startYmd, endYmd) {
  const start = ymdToUtc(startYmd);
  const end = ymdToUtc(endYmd);
  if (!start || !end || start > end) {
    return {
      enrolledDays: 0,
      openAtPeriodEnd: false,
      onHoldAtPeriodEnd: false,
      terminalEnrollmentEndedInPeriod: false,
    };
  }

  const rpmPrograms = (Array.isArray(programs) ? programs : []).filter(isRpmProgram);
  const enrolledDates = new Set();
  let openAtPeriodEnd = false;
  let endedInPeriod = false;

  for (const program of rpmPrograms) {
    for (const session of Array.isArray(program.sessions) ? program.sessions : []) {
      const enrollment = formatReportDateYmd(session?.enrollmentDate);
      if (!enrollment) continue;
      const discharge = formatReportDateYmd(session?.dischargeDate);
      const closed = formatReportDateYmd(session?.closedDate);
      const terminal = [discharge, closed].filter(Boolean).sort()[0] || '';
      if (enrollment <= endYmd && (!terminal || terminal > endYmd)) openAtPeriodEnd = true;
      if (terminal && terminal >= startYmd && terminal <= endYmd) endedInPeriod = true;

      for (let cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
        const day = cursor.toISOString().slice(0, 10);
        if (enrollment <= day && (!terminal || terminal >= day)) enrolledDates.add(day);
      }
    }
  }

  let onHoldAtPeriodEnd = false;
  if (openAtPeriodEnd) {
    const cutoff = new Date(`${endYmd}T23:59:59.999Z`);
    onHoldAtPeriodEnd = rpmPrograms.some((program) => {
      const openSession = (Array.isArray(program.sessions) ? program.sessions : [])
        .filter((session) => {
          const enrollment = formatReportDateYmd(session?.enrollmentDate);
          const discharge = formatReportDateYmd(session?.dischargeDate);
          const closed = formatReportDateYmd(session?.closedDate);
          return enrollment && enrollment <= endYmd
            && (!discharge || discharge > endYmd)
            && (!closed || closed > endYmd);
        })
        .sort((a, b) => (Number(b.sessionNumber) || 0) - (Number(a.sessionNumber) || 0))[0];
      if (!openSession) return false;
      const audits = auditsByProgram?.get?.(`${patientId}|${program.id || ''}`) || [];
      return isSessionOnHoldAt(audits, program.sessions, openSession, cutoff, 'lte');
    });
  }

  return {
    enrolledDays: enrolledDates.size,
    openAtPeriodEnd,
    onHoldAtPeriodEnd,
    terminalEnrollmentEndedInPeriod: endedInPeriod && !openAtPeriodEnd,
  };
}

function localDayBounds(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return {
    dayStart: new Date(y, m - 1, d, 0, 0, 0, 0),
    dayEnd: new Date(y, m - 1, d, 23, 59, 59, 999),
  };
}

function nextYmd(ymd) {
  const [y, m, d] = ymd.split('-').map(Number);
  return ymdLocal(new Date(y, m - 1, d + 1, 12, 0, 0, 0));
}

/**
 * Whether the patient had an Active RPM status (enrolled and not on hold) on at
 * least one day of [startYmd, endYmd]. Enrollment and discharge/closed days
 * count as active days; a day counts as on hold only when the patient was on
 * hold at both its start and its end.
 *
 * @returns {{ active: boolean, activeDays: number, firstActiveYmd: string, lastActiveYmd: string }}
 */
export function rpmActiveDuringPeriod(programs, auditsByProgram, patientId, startYmd, endYmd) {
  const result = { active: false, activeDays: 0, firstActiveYmd: '', lastActiveYmd: '' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startYmd || '') || !/^\d{4}-\d{2}-\d{2}$/.test(endYmd || '')) return result;
  if (startYmd > endYmd) return result;

  const rpmPrograms = (Array.isArray(programs) ? programs : []).filter(isRpmProgram);
  if (!rpmPrograms.length) return result;

  const programWindows = rpmPrograms.map((program) => ({
    audits: auditsByProgram?.get?.(`${patientId}|${program.id || ''}`) || [],
    windows: (Array.isArray(program.sessions) ? program.sessions : [])
      .map((session) => {
        const enrollment = formatReportDateYmd(session?.enrollmentDate);
        if (!enrollment) return null;
        const discharge = formatReportDateYmd(session?.dischargeDate);
        const closed = formatReportDateYmd(session?.closedDate);
        const terminal = [discharge, closed].filter(Boolean).sort()[0] || '';
        return { enrollment, terminal, floorYmd: priorSessionEndYmd(program.sessions, session) };
      })
      .filter(Boolean),
  }));

  for (let day = startYmd, guard = 0; day <= endYmd && guard < 400; day = nextYmd(day), guard += 1) {
    const { dayStart, dayEnd } = localDayBounds(day);
    const activeToday = programWindows.some(({ audits, windows }) => windows.some((w) => {
      if (!(w.enrollment <= day && (!w.terminal || w.terminal >= day))) return false;
      const opts = { sessionFloorYmd: w.floorYmd };
      return !isOnHoldAt(audits, dayStart, 'lt', opts) || !isOnHoldAt(audits, dayEnd, 'lte', opts);
    }));
    if (!activeToday) continue;
    result.activeDays += 1;
    if (!result.firstActiveYmd) result.firstActiveYmd = day;
    result.lastActiveYmd = day;
  }
  result.active = result.activeDays > 0;
  return result;
}

/**
 * Find an open enrolled RPM session that is on hold at as-of (if any).
 */
export function findOnHoldRpmAtAsOf(programs, auditsByProgram, patientId, asOf) {
  const mode = asOf?.mode === 'start' ? 'start' : 'end';
  const ymd = asOf?.ymd;
  const cutoff = asOf?.cutoffDate;
  const holdMode = mode === 'start' ? 'lt' : 'lte';
  if (!ymd || !cutoff) return null;

  const list = Array.isArray(programs) ? programs : [];
  for (const program of list) {
    if (!isRpmProgram(program)) continue;
    const session = findOpenSessionAt(program.sessions, ymd, mode);
    if (!session) continue;
    const programAudits = auditsByProgram.get(`${patientId}|${program.id || ''}`) || [];
    if (!isSessionOnHoldAt(programAudits, program.sessions, session, cutoff, holdMode)) continue;
    return { program, session };
  }
  return null;
}

/**
 * Pick the RPM session that best represents program state at asOf when there is
 * no open enrolled session (discharged / closed / pre-service / outreach).
 */
function pickRpmSessionForStatusAtAsOf(sessions, ymd) {
  const list = Array.isArray(sessions) ? sessions : [];
  if (!list.length) return null;

  const relevant = list.filter((s) => {
    const enroll = formatReportDateYmd(s?.enrollmentDate);
    const scheduled = formatReportDateYmd(s?.scheduledEnrollmentDate);
    const closed = formatReportDateYmd(s?.closedDate);
    const discharge = formatReportDateYmd(s?.dischargeDate);
    if (enroll && enroll <= ymd) return true;
    if (scheduled && scheduled <= ymd) return true;
    if (closed && closed <= ymd) return true;
    if (discharge && discharge <= ymd) return true;
    // Unstarted outreach / pre-service session still current if not closed yet
    if (!enroll && (!closed || closed > ymd)) return true;
    return false;
  });
  const pool = relevant.length ? relevant : list;
  const sorted = [...pool].sort(
    (a, b) => (Number(b.sessionNumber) || 0) - (Number(a.sessionNumber) || 0),
  );
  return sorted[0] || null;
}

function statusFromSessionAtAsOf(session, ymd) {
  if (!session) return '';
  const closed = formatReportDateYmd(session.closedDate);
  if (closed && closed <= ymd) return 'Closed';

  const enroll = formatReportDateYmd(session.enrollmentDate);
  const discharge = formatReportDateYmd(session.dischargeDate);
  if (discharge && discharge <= ymd && enroll && enroll <= ymd) return 'Discharged';

  if (!enroll || enroll > ymd) {
    const scheduled = formatReportDateYmd(session.scheduledEnrollmentDate);
    if (scheduled) return 'Pre-Service';
    return 'Outreach';
  }
  return '';
}

/**
 * RPM program status display label as of period end (or start).
 * Labels: Active, On Hold, Pre-Service, Outreach, Discharged, Closed.
 * Empty string when the patient has no RPM program.
 *
 * @param {Map} auditsByProgram - from groupHoldAuditsByProgram
 * @param {object} asOf - { mode, ymd, cutoffDate }
 * @returns {string}
 */
export function resolveRpmStatusAtAsOf(programs, auditsByProgram, patientId, asOf) {
  const ymd = asOf?.ymd;
  if (!ymd) return '';

  const list = (Array.isArray(programs) ? programs : []).filter(isRpmProgram);
  if (!list.length) return '';

  if (findActiveRpmAtAsOf(programs, auditsByProgram, patientId, asOf)?.session) {
    return 'Active';
  }
  if (findOnHoldRpmAtAsOf(programs, auditsByProgram, patientId, asOf)?.session) {
    return 'On Hold';
  }

  for (const program of list) {
    const session = pickRpmSessionForStatusAtAsOf(program.sessions, ymd);
    const label = statusFromSessionAtAsOf(session, ymd);
    if (label) return label;
  }
  return '';
}
